export type SizeChartUnit = 'cm' | 'mm' | 'kg' | 'jin' | 'unknown';
export type SizeChartMeasurement = 'body' | 'garment' | 'flat' | 'unknown';
export type SizeChartColumnDTO = { id: string; label: string; labelRu: string; unit: SizeChartUnit; measurement: SizeChartMeasurement };
export type SizeChartUnitEvidenceDTO = { sourceImageUrl: string; unit: Exclude<SizeChartUnit, 'unknown'>;
  scope: 'column' | 'table' | 'tolerance'; columnIds: string[]; quote: string };
export type SizeChartEstimateDTO = { size: string; columnId: string; basis: string; basedOnSizes: string[] };
export type SizeRecommendationDTO = {
  status: 'ready' | 'needs_review' | 'not_found';
  sourceImageUrls: string[];
  heightUnit: 'cm' | 'mm' | 'unknown';
  weightUnit: 'kg' | 'jin' | 'unknown';
  heightBands: string[];
  rows: Array<{ weight: string; sizes: string[] }>;
  retainedSizes: string[];
  notes: string[];
};
export type SizeChartCoverageDTO = {
  candidateImageUrls: string[]; scannedImageUrls: string[]; unavailableImageUrls: string[]; unscannedImageUrls: string[];
};
export type SizeChartConflictDTO = { field: string; sourceValue: string; chartValue: string; note: string };
export type SizeChartDTO = {
  schemaVersion: 1;
  status: 'ready' | 'needs_review' | 'not_found';
  sourceImageUrls: string[];
  columns: SizeChartColumnDTO[];
  rows: Array<{ size: string; values: Record<string, string> }>;
  missingSizes: string[];
  notes: string[];
  platformTableStatus: 'pending_binding';
  imageAssetId?: string;
  imageRenderVersion?: number;
  recommendation?: SizeRecommendationDTO;
  coverage?: SizeChartCoverageDTO;
  conflicts?: SizeChartConflictDTO[];
  referenceOnly?: boolean;
  unitEvidence?: SizeChartUnitEvidenceDTO[];
  /** Cell-level AI estimates, kept distinct from supplier/source measurements. */
  estimates?: SizeChartEstimateDTO[];
};

const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
const array = (value: unknown): unknown[] => Array.isArray(value) ? value : [];
const text = (value: unknown, max = 200): string => typeof value === 'string' ? value.trim().slice(0, max) : '';
const sizeKey = (value: string): string => value.replace(/\s+/g, '').toUpperCase();
const uniqueText = (value: unknown, max = 400, count = 12): string[] => [...new Set(array(value).map(v => text(v, max)).filter(Boolean))].slice(0, count);
const sourceUrls = (value: unknown, allowed: string[], count = 45): string[] => uniqueText(value, 2000, count).filter(url => allowed.includes(url) && url.startsWith('https://'));
const validRecommendationLabel = (value: string): boolean => !!value && /^(?:[a-z0-9][a-z0-9\s./+()\-–—]*|均码|通码|均码\/通码)$/iu.test(value);

export function isSizeChartEstimatedCell(chart: SizeChartDTO, size: string, columnId: string): boolean {
  return !!chart.estimates?.some(cell => sizeKey(cell.size) === sizeKey(size) && cell.columnId === columnId);
}

/** Keep supplier qualifiers as evidence; only a finite numeric vocabulary is displayable. */
export function formatSizeChartValueRu(value: string): string {
  const approximate = /(?:左右|上下)$/u.test(value.trim());
  const normalized = value.trim().replace(/^(?:大概|大约|约为|约|около|примерно)\s*/iu, '≈')
    .replace(/\s*(?:左右|上下)$/u, '').replace(/^[~～]/u, '≈')
    .replace(/\s*以上$/u, '+').replace(/\s*以下$/u, ' и менее');
  return approximate && !normalized.startsWith('≈') ? `≈${normalized}` : normalized;
}
export function isSizeChartValue(value: string): boolean {
  const normalized = formatSizeChartValueRu(value).replace(/ и менее$/u, '');
  return !!normalized && /^[0-9\s.,\-–—~～/±≈<>≤≥+]+$/u.test(normalized) && /\d/u.test(normalized);
}

/** A unit repeated in a cell belongs in the existing column, not in the number. */
function measurementValue(value: string, unit: SizeChartUnit): string {
  const suffix = { cm: /\s*(?:cm|厘米|公分|см)\s*$/iu, mm: /\s*(?:mm|毫米|мм)\s*$/iu,
    kg: /\s*(?:kg|公斤|千克|кг)\s*$/iu, jin: /\s*(?:斤|jin|цзинь)\s*$/iu, unknown: /$^/u };
  const numeric = value.replace(suffix[unit], '').trim();
  return isSizeChartValue(numeric) ? numeric : '';
}

/** A same-table caption is evidence; an arbitrary note or usual clothing unit is not. */
function normalizeUnitEvidence(value: unknown, columns: SizeChartColumnDTO[], sources: string[]): SizeChartUnitEvidenceDTO[] {
  const result: SizeChartUnitEvidenceDTO[] = [];
  for (const raw of array(value).slice(0, 24)) {
    const item = record(raw), quote = text(item['quote'], 400), url = text(item['sourceImageUrl'], 2000);
    const unit = item['unit'], scope = item['scope'];
    if (!sources.includes(url) || !['cm','mm','kg','jin'].includes(String(unit)) || !['column','table','tolerance'].includes(String(scope))
      || !quote || /包装|包裹|运费|物流|快递|邮寄|shipping|packag|parcel|упаков|достав/iu.test(quote)
      || /(?:不是|并非|非|not\s+|не\s+).{0,8}(?:cm|mm|厘米|毫米|公分|см|мм)/iu.test(quote)) continue;
    const length = unit === 'cm' || unit === 'mm';
    const mentioned = length
      ? [...(quote.match(/(?<![a-z])cm(?![a-z])|厘米|公分|сантиметр\p{L}*|(?<!\p{L})см(?!\p{L})/giu) ?? []).map(()=>'cm'),
        ...(quote.match(/(?<![a-z])mm(?![a-z])|毫米|миллиметр\p{L}*|(?<!\p{L})мм(?!\p{L})/giu) ?? []).map(()=>'mm')]
      : [...(quote.match(/(?<![a-z])kg(?![a-z])|公斤|千克|килограмм\p{L}*|(?<!\p{L})кг(?!\p{L})/giu) ?? []).map(()=>'kg'),
        ...(quote.match(/(?<![公千])斤|(?<![a-z])jin(?![a-z])|цзинь/giu) ?? []).map(()=>'jin')];
    if (new Set(mentioned).size !== 1 || !mentioned.includes(String(unit))) continue;
    if (scope === 'tolerance' && (!length || !/误差|誤差|偏差|tolerance|погрешност|отклонени|measurement.{0,30}error/iu.test(quote))) continue;
    const columnIds = uniqueText(item['columnIds'], 40, 12).filter(id => {
      const column = columns.find(c => c.id === id); if (!column) return false;
      const label = `${column.id} ${column.label} ${column.labelRu}`;
      const weight = /weight|重量|体重|體重|推荐体重|вес|масс/iu.test(label);
      const dimension = /length|width|height|waist|hip|chest|bust|shoulder|sleeve|inseam|围|圍|长|長|宽|寬|身高|длин|ширин|рост|обхват/iu.test(label);
      return length ? dimension && !weight : weight;
    });
    if (columnIds.length) result.push({ sourceImageUrl: url, unit: unit as SizeChartUnitEvidenceDTO['unit'],
      scope: scope as SizeChartUnitEvidenceDTO['scope'], columnIds, quote });
  }
  return result;
}

export function normalizeSizeRecommendation(value: unknown, expectedSizes: string[], allowedImageUrls: string[]): SizeRecommendationDTO {
  const input = record(value), sources = sourceUrls(input['sourceImageUrls'], allowedImageUrls);
  const heightBands = array(input['heightBands']).slice(0, 20).map(v => text(v, 40));
  let invalid = heightBands.some(v => !isSizeChartValue(v));
  const rows = array(input['rows']).slice(0, 30).map(raw => {
    const row = record(raw), weight = text(row['weight'], 40);
    const sizes = array(row['sizes']).slice(0, 20).map(v => {
      const label = text(v, 60);
      // Recommendation alternatives are text, never new SKU rows or inferred size mappings.
      if (!validRecommendationLabel(label)) { invalid = true; return ''; }
      return label;
    });
    if (!isSizeChartValue(weight) || sizes.length !== heightBands.length) invalid = true;
    return { weight, sizes };
  });
  const heightUnit = input['heightUnit'] === 'cm' || input['heightUnit'] === 'mm' ? input['heightUnit'] : 'unknown';
  const weightUnit = input['weightUnit'] === 'kg' || input['weightUnit'] === 'jin' ? input['weightUnit'] : 'unknown';
  const hasTable = sources.length > 0 && heightBands.length > 0 && rows.length > 0;
  const complete = hasTable && !invalid && heightUnit !== 'unknown' && weightUnit !== 'unknown' && expectedSizes.length > 0;
  return { status: complete && input['status'] !== 'needs_review' ? 'ready' : hasTable ? 'needs_review' : 'not_found',
    sourceImageUrls: sources, heightUnit, weightUnit, heightBands, rows,
    retainedSizes: [...new Set(expectedSizes.map(s => s.trim()).filter(Boolean))], notes: uniqueText(input['notes']) };
}

export function canRenderSizeChart(chart: SizeChartDTO): boolean {
  const measurement = chart.sourceImageUrls.length > 0 && chart.rows.length > 0 && chart.rows.length <= 100 && chart.columns.length > 0 && chart.columns.length <= 12
    && ((chart.missingSizes.length === 0 && chart.status === 'ready') || (chart.referenceOnly === true && chart.status === 'needs_review'))
    && chart.columns.every(c => !!c.labelRu && !/[\u3400-\u9fff]/u.test(c.labelRu) && c.unit !== 'unknown'
      && (c.measurement !== 'unknown' || chart.referenceOnly === true))
    && chart.rows.every(r => chart.columns.every(c => isSizeChartValue(r.values[c.id] ?? '')));
  const r = chart.recommendation;
  const recommendation = r?.status === 'ready' && r.sourceImageUrls.length > 0 && r.heightBands.length > 0 && r.heightBands.length <= 20
    && r.heightBands.every(isSizeChartValue) && r.rows.length > 0 && r.rows.length <= 30 && r.retainedSizes.length > 0
    && r.heightUnit !== 'unknown' && r.weightUnit !== 'unknown'
    && r.rows.every(row => isSizeChartValue(row.weight) && row.sizes.length === r.heightBands.length && row.sizes.every(validRecommendationLabel));
  return measurement || recommendation;
}

/** Shape/evidence validation for an optional measurement table, never a SKU gate. */
export function normalizeSizeChart(value: unknown, expectedSizes: string[], allowedImageUrls: string[]): SizeChartDTO {
  const input = record(value);
  const sources = sourceUrls(input['sourceImageUrls'], allowedImageUrls);
  const columns: SizeChartColumnDTO[] = [];
  for (const raw of array(input['columns']).slice(0, 12)) {
    const col = record(raw), id = text(col['id'], 40), label = text(col['label'], 80), labelRu = text(col['labelRu'], 80);
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(id) || !label || !labelRu || columns.some(c => c.id === id)) continue;
    columns.push({ id, label, labelRu,
      unit: ['cm','mm','kg','jin'].includes(String(col['unit'])) ? col['unit'] as SizeChartUnit : 'unknown',
      measurement: ['body','garment','flat'].includes(String(col['measurement'])) ? col['measurement'] as SizeChartMeasurement : 'unknown' });
  }
  const unitEvidence = normalizeUnitEvidence(input['unitEvidence'], columns, sources);
  const inferredUnits: string[] = [];
  for (const column of columns) {
    if (column.unit !== 'unknown') continue;
    const units = [...new Set(unitEvidence.filter(e => e.columnIds.includes(column.id)).map(e => e.unit))];
    if (units.length === 1) { column.unit = units[0]!; inferredUnits.push(column.label); }
  }
  const sizes = [...new Set(expectedSizes.map(v => v.trim()).filter(Boolean))];
  const rows: SizeChartDTO['rows'] = [];
  let ambiguous = false;
  for (const raw of array(input['rows']).slice(0, 100)) {
    const row = record(raw), original = text(row['size'], 40);
    const matches = sizes.filter(s => sizeKey(s) === sizeKey(original));
    const size = matches.length === 1 ? matches[0]! : original;
    if (!size || (sizes.length && matches.length === 0)) continue;
    if (matches.length > 1) { ambiguous = true; continue; }
    if (rows.some(r => sizeKey(r.size) === sizeKey(size))) { ambiguous = true; continue; }
    const values: Record<string, string> = {}, rawValues = record(row['values']);
    for (const col of columns) {
      const rawValue = rawValues[col.id];
      const v = typeof rawValue === 'number' && Number.isFinite(rawValue) ? String(rawValue) : text(rawValue, 40);
      values[col.id] = measurementValue(v, col.unit);
    }
    rows.push({ size, values });
  }
  if (sizes.length) rows.sort((a,b) => sizes.findIndex(s => sizeKey(s) === sizeKey(a.size)) - sizes.findIndex(s => sizeKey(s) === sizeKey(b.size)));
  const missingSizes = sizes.filter(s => !rows.some(r => sizeKey(r.size) === sizeKey(s)));
  const estimates: SizeChartEstimateDTO[] = [];
  for (const raw of array(input['estimates']).slice(0, 1200)) {
    const estimate = record(raw), row = rows.find(item => sizeKey(item.size) === sizeKey(text(estimate['size'], 40)));
    const columnId = text(estimate['columnId'], 40), basis = text(estimate['basis'], 500);
    if (!row || !basis || !columns.some(column => column.id === columnId) || !row.values[columnId]
      || estimates.some(item => item.size === row.size && item.columnId === columnId)) continue;
    estimates.push({ size: row.size, columnId, basis, basedOnSizes: uniqueText(estimate['basedOnSizes'], 40, 100)
      .filter(size => sizes.some(known => sizeKey(known) === sizeKey(size))) });
  }
  const notes = array(input['notes']).map(v => text(v, 400)).filter(Boolean).slice(0, 12);
  if (inferredUnits.length) notes.unshift(`单位依据同一尺码表图片上的原文说明补全：${inferredUnits.join('、')}；原始数值未换算。`);
  const hasTable = rows.length > 0 && columns.length > 0 && sources.length > 0;
  const complete = hasTable && sizes.length > 0 && !ambiguous && !missingSizes.length && columns.every(c => c.unit !== 'unknown' && c.measurement !== 'unknown'
    && !/[\u3400-\u9fff]/u.test(c.labelRu)) && rows.every(r => columns.every(c => !!r.values[c.id]));
  const conflicts = array(input['conflicts']).slice(0, 12).flatMap(raw => {
    const item = record(raw), field = text(item['field'], 100), sourceValue = text(item['sourceValue'], 400), chartValue = text(item['chartValue'], 400), note = text(item['note'], 400);
    return field && sourceValue && chartValue ? [{ field, sourceValue, chartValue, note }] : [];
  });
  const requestedReview = estimates.length > 0 || input['status'] === 'needs_review' || input['needsReview'] === true || input['referenceOnly'] === true
    || conflicts.some(conflict => !conflict.field.startsWith('recommendation.'));
  const coverageInput = record(input['coverage']);
  const candidates = sourceUrls(coverageInput['candidateImageUrls'], allowedImageUrls, 150);
  const scanned = sourceUrls(coverageInput['scannedImageUrls'], candidates);
  const unavailable = sourceUrls(coverageInput['unavailableImageUrls'], candidates).filter(u => !scanned.includes(u));
  return { schemaVersion: 1, status: complete && !requestedReview ? 'ready' : hasTable ? 'needs_review' : 'not_found',
    sourceImageUrls: sources, columns, rows, missingSizes, notes: [...new Set(notes)].slice(0, 12), platformTableStatus: 'pending_binding',
    ...(input['recommendation'] && typeof input['recommendation'] === 'object' ? { recommendation: normalizeSizeRecommendation(input['recommendation'], sizes, allowedImageUrls) } : {}),
    ...(input['coverage'] && typeof input['coverage'] === 'object' ? { coverage: { candidateImageUrls: candidates, scannedImageUrls: scanned, unavailableImageUrls: unavailable,
      unscannedImageUrls: candidates.filter(u => !scanned.includes(u) && !unavailable.includes(u)) } } : {}),
    ...(conflicts.length ? { conflicts } : {}),
    ...(input['referenceOnly'] === true || estimates.length ? { referenceOnly: true } : {}),
    ...(unitEvidence.length ? { unitEvidence } : {}),
    ...(Array.isArray(input['estimates']) ? { estimates } : {}),
    ...(typeof input['imageRenderVersion'] === 'number' && Number.isInteger(input['imageRenderVersion']) && input['imageRenderVersion'] > 0
      ? { imageRenderVersion: input['imageRenderVersion'] } : {}),
    ...(typeof input['imageAssetId'] === 'string' && input['imageAssetId'] ? { imageAssetId: input['imageAssetId'] } : {}) };
}

export function sizeChartTsv(chart: SizeChartDTO): string {
  const cell = (v: string) => { const cleaned = v.replace(/[\t\r\n]/g, ' '); return /^[=+@-]/.test(cleaned) ? `'${cleaned}` : cleaned; };
  const rows: string[][] = chart.columns.length ? [['Размер', ...chart.columns.map(c => `${c.labelRu} (${c.unit})`)],
    ...chart.rows.map(row => [row.size, ...chart.columns.map(c => {
      const value = formatSizeChartValueRu(row.values[c.id] ?? '');
      return isSizeChartEstimatedCell(chart, row.size, c.id) ? `${value.startsWith('≈') ? '' : '≈'}${value}*` : value;
    })])] : [];
  if (chart.estimates?.length) rows.push(['* Расчётная оценка ИИ, не измерение поставщика. Сравните с собственной одеждой.']);
  const r = chart.recommendation;
  if (r?.heightBands.length && r.rows.length) {
    if (rows.length) rows.push([]);
    rows.push(['Рекомендации поставщика по выбору размера'],
      [`Вес (${r.weightUnit === 'jin' ? 'цзинь' : r.weightUnit === 'kg' ? 'кг' : '?'}) / Рост (${r.heightUnit === 'cm' ? 'см' : r.heightUnit === 'mm' ? 'мм' : '?'})`, ...r.heightBands.map(formatSizeChartValueRu)],
      ...r.rows.map(row => [formatSizeChartValueRu(row.weight), ...row.sizes]));
  }
  return rows.map(row => row.map(cell).join('\t')).join('\n');
}
